// Dashboard.tsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Users,
  UserCheck,
  UserX,
  Building2,
  Shield,
  Key,
  BarChart3,
  PieChart,
  Info,
} from "lucide-react";
import { useSelector } from "react-redux";
import type { RootState } from "../../store/store";
import { API_V1_BASE_URL } from "../../config/api";

// ---------- Types ----------
interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  status: string;
  role: string;
  role_id: string;
  tenant_id: string;
  tenant_name?: string;
  created_at?: string;
}

interface Role {
  id: string;
  role_name: string;
  description: string;
  permissions: string[];
  is_active: boolean;
}

interface Permission {
  permission_code: string;
  module: string;
  description: string;
}

interface Tenant {
  id: string;
  name: string;
  status: string;
  max_users?: number;
  created_at?: string;
}

interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  deactivatedUsers: number;
  totalTenants: number;
  activeTenants: number;
  inactiveTenants: number;
  totalRoles: number;
  totalPermissions: number;
}

interface MonthlyData {
  month: string;
  active: number;
  deactivated: number;
}

const BAR_COLORS = [
  "#3B82F6", // blue
  "#10B981", // green
  "#8B5CF6", // purple
  "#F59E0B", // amber
  "#EF4444", // red
  "#6366F1", // indigo
  "#EC4899", // pink
  "#14B8A6", // teal
  "#F97316", // orange
  "#6B7280", // gray
];

const normalizeArray = (response: any): any[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.users)) return response.users;
  if (Array.isArray(response?.roles)) return response.roles;
  if (Array.isArray(response?.permissions)) return response.permissions;
  if (Array.isArray(response?.tenants)) return response.tenants;
  console.warn("Unexpected API response shape:", response);
  return [];
};

const formatRoleName = (name: string): string => {
  if (!name) return "";
  return name
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const computeMonthlyGrowth = (usersData: User[]): MonthlyData[] => {
  const months: { month: string; year: number; monthIndex: number }[] = [];
  const now = new Date();
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      month: d.toLocaleString("default", { month: "short" }),
      year: d.getFullYear(),
      monthIndex: d.getMonth(),
    });
  }

  return months.map(({ month, year, monthIndex }) => {
    const usersUpToMonth = usersData.filter((u) => {
      if (!u.created_at) return false;
      const created = new Date(u.created_at);
      return (
        created.getFullYear() < year ||
        (created.getFullYear() === year && created.getMonth() <= monthIndex)
      );
    });
    const active = usersUpToMonth.filter(
      (u) => u.status?.toLowerCase() === "active",
    ).length;
    const deactivated = usersUpToMonth.filter(
      (u) => u.status?.toLowerCase() !== "active",
    ).length;
    return { month, active, deactivated };
  });
};

// ---------- Main Dashboard Component ----------
const SuperAdminDashboard: React.FC = () => {
  const { accessToken: token, isLoading: authLoading } = useSelector(
    (state: RootState) => state.auth,
  );

  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    activeUsers: 0,
    deactivatedUsers: 0,
    totalTenants: 0,
    activeTenants: 0,
    inactiveTenants: 0,
    totalRoles: 0,
    totalPermissions: 0,
  });

  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [hoveredDataPoint, setHoveredDataPoint] = useState<{
    index: number;
    type: "active" | "deactivated";
  } | null>(null);

  const api = {
    getUsers: async (): Promise<User[]> => {
      const response = await fetch(
        `${API_V1_BASE_URL}/admin/users?limit=1000`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch users");
      }
      const data = await response.json();
      return normalizeArray(data);
    },
    getRoles: async (): Promise<Role[]> => {
      const response = await fetch(`${API_V1_BASE_URL}/admin/roles`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch roles");
      }
      const data = await response.json();
      return normalizeArray(data);
    },
    getPermissions: async (): Promise<Permission[]> => {
      const response = await fetch(`${API_V1_BASE_URL}/admin/permissions`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch permissions");
      }
      const data = await response.json();
      return normalizeArray(data);
    },
    getTenants: async (): Promise<Tenant[]> => {
      const response = await fetch(`${API_V1_BASE_URL}/admin/tenants`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch tenants");
      }
      const data = await response.json();
      return normalizeArray(data);
    },
  };

  useEffect(() => {
    if (authLoading) return;

    const fetchAllData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!token) {
          throw new Error("No authentication token found. Please login again.");
        }

        const [usersData, rolesData, permissionsData, tenantsData] =
          await Promise.all([
            api.getUsers(),
            api.getRoles(),
            api.getPermissions(),
            api.getTenants(),
          ]);

        const safeUsers = Array.isArray(usersData) ? usersData : [];
        const safeRoles = Array.isArray(rolesData) ? rolesData : [];
        const safePermissions = Array.isArray(permissionsData)
          ? permissionsData
          : [];
        const safeTenants = Array.isArray(tenantsData) ? tenantsData : [];

        setUsers(safeUsers);
        setRoles(safeRoles);
        setPermissions(safePermissions);
        setTenants(safeTenants);

        const activeUsers = safeUsers.filter(
          (u: User) => u.status?.toLowerCase() === "active",
        ).length;
        const deactivatedUsers = safeUsers.filter(
          (u: User) => u.status?.toLowerCase() !== "active",
        ).length;
        const activeTenants = safeTenants.filter(
          (t: Tenant) => t.status?.toLowerCase() === "active",
        ).length;
        const inactiveTenants = safeTenants.filter(
          (t: Tenant) => t.status?.toLowerCase() !== "active",
        ).length;

        setStats({
          totalUsers: safeUsers.length,
          activeUsers,
          deactivatedUsers,
          totalTenants: safeTenants.length,
          activeTenants,
          inactiveTenants,
          totalRoles: safeRoles.length,
          totalPermissions: safePermissions.length,
        });

        setMonthlyData(computeMonthlyGrowth(safeUsers));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch data");
        console.error("Error fetching dashboard data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, authLoading]);

  // ----- Derived / memoized values -----
  const activeUserPercentage =
    stats.totalUsers > 0
      ? Math.round((stats.activeUsers / stats.totalUsers) * 100)
      : 0;

  const deactivatedUserPercentage =
    stats.totalUsers > 0
      ? Math.round((stats.deactivatedUsers / stats.totalUsers) * 100)
      : 0;

  const activeTenantPercentage =
    stats.totalTenants > 0
      ? Math.round((stats.activeTenants / stats.totalTenants) * 100)
      : 0;

  const tenantsWithCounts = useMemo(() => {
    return tenants
      .map((tenant) => ({
        ...tenant,
        userCount: users.filter((u) => u.tenant_id === tenant.id).length,
      }))
      .sort((a, b) => b.userCount - a.userCount);
  }, [tenants, users]);

  const roleDistribution = useMemo(() => {
    return roles
      .map((role) => ({
        ...role,
        count: users.filter((u) => u.role_id === role.id).length,
      }))
      .sort((a, b) => b.count - a.count);
  }, [roles, users]);

  const maxTenantUsers = useMemo(
    () => Math.max(1, ...tenantsWithCounts.map((t) => t.userCount)),
    [tenantsWithCounts],
  );

  const maxRoleCount = useMemo(
    () => Math.max(1, ...roleDistribution.map((r) => r.count)),
    [roleDistribution],
  );

  const chartMax = useMemo(
    () =>
      Math.max(
        1,
        ...monthlyData.flatMap((d) => [d.active, d.deactivated]),
      ),
    [monthlyData],
  );

  // ----- Loading -----
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" />
          <p className="mt-4 text-gray-600 font-medium">
            Loading dashboard...
          </p>
        </div>
      </div>
    );
  }

  // ----- Error -----
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 max-w-md w-full">
          <div className="text-red-500 text-center">
            <div className="text-4xl mb-3">⚠️</div>
            <p className="text-lg font-semibold text-gray-900">
              Error Loading Dashboard
            </p>
            <p className="mt-2 text-sm text-gray-600">{error}</p>
            <div className="mt-6 flex gap-3 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                Retry
              </button>
              {error.includes("token") && (
                <button
                  onClick={() => (window.location.href = "/login")}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
                >
                  Go to Login
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ----- Render -----
  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6">
      <div className="max-w-[1600px] mx-auto">
        {/* Title */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
            Dashboard Overview
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            A summary of users, tenants, roles and permissions across the
            platform
          </p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
          <KpiCard
            icon={<Users className="w-5 h-5" />}
            label="Total Users"
            value={stats.totalUsers}
            subtitle="All users in platform"
            accent="blue"
          />
          <KpiCard
            icon={<UserCheck className="w-5 h-5" />}
            label="Active Users"
            value={stats.activeUsers}
            subtitle={`${activeUserPercentage}% of total`}
            accent="green"
          />
          <KpiCard
            icon={<UserX className="w-5 h-5" />}
            label="Deactivated Users"
            value={stats.deactivatedUsers}
            subtitle={`${deactivatedUserPercentage}% of total`}
            accent="red"
          />
          <KpiCard
            icon={<Building2 className="w-5 h-5" />}
            label="Total Tenants"
            value={stats.totalTenants}
            subtitle={`${stats.activeTenants} active, ${stats.inactiveTenants} inactive`}
            accent="purple"
          />
          <KpiCard
            icon={<Shield className="w-5 h-5" />}
            label="Roles"
            value={stats.totalRoles}
            subtitle="Custom & default"
            accent="indigo"
          />
          <KpiCard
            icon={<Key className="w-5 h-5" />}
            label="Permissions"
            value={stats.totalPermissions}
            subtitle="Total permissions"
            accent="amber"
          />
        </div>

        {/* Analytics Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* User Growth */}
          <Card>
            <CardHeader
              icon={<BarChart3 className="w-5 h-5 text-blue-600" />}
              title="User Growth"
              subtitle="Active vs deactivated users over last 7 months"
            />
            <div className="relative h-64">
              {monthlyData.length > 0 ? (
                <>
                  <svg
                    className="w-full h-full"
                    viewBox="0 0 600 250"
                    preserveAspectRatio="none"
                  >
                    {[0, 0.25, 0.5, 0.75, 1].map((frac) => {
                      const y = 200 - frac * 190;
                      const label = Math.round(frac * chartMax);
                      return (
                        <g key={frac}>
                          <line
                            x1="40"
                            y1={y}
                            x2="580"
                            y2={y}
                            stroke="#E5E7EB"
                            strokeWidth="1"
                            strokeDasharray="4 4"
                          />
                          <text
                            x="35"
                            y={y + 4}
                            textAnchor="end"
                            className="text-xs fill-gray-400"
                          >
                            {label}
                          </text>
                        </g>
                      );
                    })}

                    {/* X labels */}
                    {monthlyData.map((data, index) => {
                      const x =
                        monthlyData.length === 1
                          ? 310
                          : 40 +
                            (index / (monthlyData.length - 1)) * 540;
                      return (
                        <text
                          key={`x-${index}`}
                          x={x}
                          y="222"
                          textAnchor="middle"
                          className="text-xs fill-gray-500"
                        >
                          {data.month}
                        </text>
                      );
                    })}

                    {/* Active area */}
                    <polygon
                      points={`40,200 ${monthlyData
                        .map((data, index) => {
                          const x =
                            monthlyData.length === 1
                              ? 310
                              : 40 +
                                (index / (monthlyData.length - 1)) * 540;
                          const y = 200 - (data.active / chartMax) * 190;
                          return `${x},${y}`;
                        })
                        .join(" ")} 580,200`}
                      fill="url(#activeGradient)"
                    />

                    {/* Active line */}
                    <polyline
                      points={monthlyData
                        .map((data, index) => {
                          const x =
                            monthlyData.length === 1
                              ? 310
                              : 40 +
                                (index / (monthlyData.length - 1)) * 540;
                          const y = 200 - (data.active / chartMax) * 190;
                          return `${x},${y}`;
                        })
                        .join(" ")}
                      fill="none"
                      stroke="#3B82F6"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />

                    {/* Deactivated line */}
                    <polyline
                      points={monthlyData
                        .map((data, index) => {
                          const x =
                            monthlyData.length === 1
                              ? 310
                              : 40 +
                                (index / (monthlyData.length - 1)) * 540;
                          const y = 200 - (data.deactivated / chartMax) * 190;
                          return `${x},${y}`;
                        })
                        .join(" ")}
                      fill="none"
                      stroke="#EF4444"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />

                    {/* Points + tooltips */}
                    {monthlyData.map((data, index) => {
                      const x =
                        monthlyData.length === 1
                          ? 310
                          : 40 +
                            (index / (monthlyData.length - 1)) * 540;
                      const activeY = 200 - (data.active / chartMax) * 190;
                      const deactiveY =
                        200 - (data.deactivated / chartMax) * 190;
                      return (
                        <g key={index}>
                          <circle
                            cx={x}
                            cy={activeY}
                            r={
                              hoveredDataPoint?.index === index &&
                              hoveredDataPoint?.type === "active"
                                ? 7
                                : 5
                            }
                            fill="#3B82F6"
                            stroke="white"
                            strokeWidth="2"
                            className="cursor-pointer"
                            onMouseEnter={() =>
                              setHoveredDataPoint({ index, type: "active" })
                            }
                            onMouseLeave={() => setHoveredDataPoint(null)}
                          />
                          <circle
                            cx={x}
                            cy={deactiveY}
                            r={
                              hoveredDataPoint?.index === index &&
                              hoveredDataPoint?.type === "deactivated"
                                ? 7
                                : 5
                            }
                            fill="#EF4444"
                            stroke="white"
                            strokeWidth="2"
                            className="cursor-pointer"
                            onMouseEnter={() =>
                              setHoveredDataPoint({
                                index,
                                type: "deactivated",
                              })
                            }
                            onMouseLeave={() => setHoveredDataPoint(null)}
                          />
                          {hoveredDataPoint?.index === index && (
                            <>
                              <rect
                                x={x - 30}
                                y={
                                  (hoveredDataPoint.type === "active"
                                    ? activeY
                                    : deactiveY) - 40
                                }
                                width="60"
                                height="26"
                                rx="6"
                                fill="white"
                                stroke="#E5E7EB"
                              />
                              <text
                                x={x}
                                y={
                                  (hoveredDataPoint.type === "active"
                                    ? activeY
                                    : deactiveY) - 22
                                }
                                textAnchor="middle"
                                className="text-xs font-semibold fill-gray-700"
                              >
                                {hoveredDataPoint.type === "active"
                                  ? data.active
                                  : data.deactivated}
                              </text>
                            </>
                          )}
                        </g>
                      );
                    })}

                    <defs>
                      <linearGradient
                        id="activeGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor="#3B82F6"
                          stopOpacity="0.25"
                        />
                        <stop
                          offset="100%"
                          stopColor="#3B82F6"
                          stopOpacity="0"
                        />
                      </linearGradient>
                    </defs>
                  </svg>
                </>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-400 text-sm">No data available</p>
                </div>
              )}
            </div>
            <div className="flex justify-center gap-6 mt-2">
              <LegendDot color="#3B82F6" label="Active Users" />
              <LegendDot color="#EF4444" label="Deactivated Users" />
            </div>
          </Card>

          {/* User Status */}
          <Card>
            <CardHeader
              icon={<PieChart className="w-5 h-5 text-blue-600" />}
              title="User Status"
              subtitle="Active vs deactivated users"
              right={<Info className="w-4 h-4 text-gray-400" />}
            />
            <div className="flex flex-col sm:flex-row items-center justify-center gap-8 py-4">
              <div className="relative w-44 h-44 flex-shrink-0">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                  {stats.totalUsers > 0 ? (
                    <>
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        stroke="#3B82F6"
                        strokeWidth="18"
                        strokeDasharray={`${(stats.activeUsers / stats.totalUsers) * 251.2} 251.2`}
                      />
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        stroke="#EF4444"
                        strokeWidth="18"
                        strokeDasharray={`${(stats.deactivatedUsers / stats.totalUsers) * 251.2} 251.2`}
                        strokeDashoffset={`-${(stats.activeUsers / stats.totalUsers) * 251.2}`}
                      />
                    </>
                  ) : (
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="#E5E7EB"
                      strokeWidth="18"
                    />
                  )}
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-2xl font-bold text-slate-900 leading-none">
                    {stats.totalUsers}
                  </span>
                  <span className="text-xs text-slate-500 mt-1">
                    Total Users
                  </span>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className="w-3 h-3 rounded-full bg-blue-500" />
                  <div>
                    <p className="text-sm text-slate-600">Active Users</p>
                    <p className="text-lg font-semibold text-slate-900">
                      {stats.activeUsers}
                    </p>
                  </div>
                  <span className="ml-4 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-600">
                    {activeUserPercentage}%
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-3 h-3 rounded-full bg-red-500" />
                  <div>
                    <p className="text-sm text-slate-600">
                      Deactivated Users
                    </p>
                    <p className="text-lg font-semibold text-slate-900">
                      {stats.deactivatedUsers}
                    </p>
                  </div>
                  <span className="ml-4 px-2 py-0.5 text-xs font-medium rounded-full bg-red-50 text-red-600">
                    {deactivatedUserPercentage}%
                  </span>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Distribution Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* Users by Tenant */}
          <Card>
            <CardHeader
              icon={<Building2 className="w-5 h-5 text-blue-600" />}
              title="Users by Tenant"
              subtitle="All tenants and user distribution"
            />
            <div className="space-y-4 max-h-80 overflow-y-auto pr-2">
              {tenantsWithCounts.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">
                  No tenants available
                </p>
              ) : (
                tenantsWithCounts.map((tenant, index) => {
                  const pct = (tenant.userCount / maxTenantUsers) * 100;
                  const color = BAR_COLORS[index % BAR_COLORS.length];
                  return (
                    <div key={tenant.id} className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold flex items-center justify-center flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-baseline mb-1 gap-2">
                          <span className="text-sm font-medium text-slate-700 truncate">
                            {tenant.name}
                          </span>
                          <span className="text-xs text-slate-500 flex-shrink-0">
                            {tenant.userCount} user
                            {tenant.userCount === 1 ? "" : "s"}
                          </span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                          <div
                            className="h-2 rounded-full transition-all"
                            style={{
                              width: `${pct}%`,
                              backgroundColor: color,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </Card>

          {/* Users by Role */}
          <Card>
            <CardHeader
              icon={<Shield className="w-5 h-5 text-blue-600" />}
              title="Users by Role"
              subtitle="Distribution of users across roles"
            />
            <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
              {roleDistribution.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">
                  No roles available
                </p>
              ) : (
                roleDistribution.map((role, index) => {
                  const pct = (role.count / maxRoleCount) * 100;
                  const color = BAR_COLORS[index % BAR_COLORS.length];
                  return (
                    <div
                      key={role.id}
                      className="grid grid-cols-[120px_1fr_32px] items-center gap-3"
                    >
                      <span
                        className="text-sm text-slate-700 truncate"
                        title={formatRoleName(role.role_name)}
                      >
                        {formatRoleName(role.role_name)}
                      </span>
                      <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                        <div
                          className="h-2.5 rounded-full transition-all"
                          style={{ width: `${pct}%`, backgroundColor: color }}
                        />
                      </div>
                      <span className="text-sm font-medium text-slate-700 text-right">
                        {role.count}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </Card>
        </div>

        {/* Tenant Status */}
        <Card>
          <CardHeader
            icon={<Users className="w-5 h-5 text-blue-600" />}
            title="Tenant Status"
            subtitle="Active vs inactive tenants"
            right={<Building2 className="w-4 h-4 text-gray-400" />}
          />
          <div className="flex flex-col lg:flex-row items-center gap-8 py-2">
            {/* Left: counts */}
            <div className="flex gap-10 flex-shrink-0">
              <div className="flex items-start gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-green-500 mt-1.5" />
                <div>
                  <p className="text-sm text-slate-600">Active</p>
                  <p className="text-xl font-bold text-slate-900">
                    {stats.activeTenants}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500 mt-1.5" />
                <div>
                  <p className="text-sm text-slate-600">Inactive</p>
                  <p className="text-xl font-bold text-slate-900">
                    {stats.inactiveTenants}
                  </p>
                </div>
              </div>
            </div>

            {/* Middle: bar */}
            <div className="flex-1 w-full">
              <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden flex">
                <div
                  className="h-3 bg-green-500 transition-all"
                  style={{ width: `${activeTenantPercentage}%` }}
                />
                <div
                  className="h-3 bg-slate-200 transition-all"
                  style={{ width: `${100 - activeTenantPercentage}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-xs text-slate-500">
                <span>{activeTenantPercentage}% active</span>
                <span>{100 - activeTenantPercentage}% inactive</span>
              </div>
            </div>

            {/* Right: ring */}
            <div className="relative w-24 h-24 flex-shrink-0">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  fill="none"
                  stroke="#E5E7EB"
                  strokeWidth="10"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  fill="none"
                  stroke="#10B981"
                  strokeWidth="10"
                  strokeDasharray={`${activeTenantPercentage * 2.64} 264`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-lg font-bold text-slate-900 leading-none">
                  {activeTenantPercentage}%
                </span>
                <span className="text-[10px] text-slate-500 mt-0.5">
                  Active
                </span>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

// ---------- Reusable UI Components ----------

interface KpiCardProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  subtitle: string;
  accent: "blue" | "green" | "red" | "purple" | "indigo" | "amber";
}

const accentClasses: Record<
  KpiCardProps["accent"],
  { iconBg: string; iconText: string; leftBorder: string; gradientBg: string }
> = {
  blue: {
    iconBg: "bg-blue-50",
    iconText: "text-blue-600",
    leftBorder: "border-l-[3px] border-l-blue-500",
    gradientBg:
      "bg-gradient-to-br from-white via-white to-blue-50/60",
  },
  green: {
    iconBg: "bg-green-50",
    iconText: "text-green-600",
    leftBorder: "border-l-[3px] border-l-green-500",
    gradientBg:
      "bg-gradient-to-br from-white via-white to-green-50/60",
  },
  red: {
    iconBg: "bg-red-50",
    iconText: "text-red-600",
    leftBorder: "border-l-[3px] border-l-red-500",
    gradientBg:
      "bg-gradient-to-br from-white via-white to-red-50/60",
  },
  purple: {
    iconBg: "bg-purple-50",
    iconText: "text-purple-600",
    leftBorder: "border-l-[3px] border-l-purple-500",
    gradientBg:
      "bg-gradient-to-br from-white via-white to-purple-50/60",
  },
  indigo: {
    iconBg: "bg-indigo-50",
    iconText: "text-indigo-600",
    leftBorder: "border-l-[3px] border-l-indigo-500",
    gradientBg:
      "bg-gradient-to-br from-white via-white to-indigo-50/60",
  },
  amber: {
    iconBg: "bg-amber-50",
    iconText: "text-amber-600",
    leftBorder: "border-l-[3px] border-l-amber-500",
    gradientBg:
      "bg-gradient-to-br from-white via-white to-amber-50/60",
  },
};

const KpiCard: React.FC<KpiCardProps> = ({
  icon,
  label,
  value,
  subtitle,
  accent,
}) => {
  const { iconBg, iconText, leftBorder, gradientBg } =
    accentClasses[accent];
  return (
    <div
      className={`rounded-xl border border-slate-200 ${leftBorder} ${gradientBg} p-4 shadow-sm hover:shadow-md transition-shadow`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div
          className={`w-10 h-10 rounded-lg ${iconBg} ${iconText} flex items-center justify-center flex-shrink-0`}
        >
          {icon}
        </div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide leading-tight">
          {label}
        </p>
      </div>
      <p className="text-2xl font-bold text-slate-900 leading-tight">
        {value}
      </p>
      <p className="text-xs text-slate-500 mt-1 truncate" title={subtitle}>
        {subtitle}
      </p>
    </div>
  );
};

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
    {children}
  </div>
);

interface CardHeaderProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  right?: React.ReactNode;
}

const CardHeader: React.FC<CardHeaderProps> = ({
  icon,
  title,
  subtitle,
  right,
}) => (
  <div className="flex items-start justify-between mb-4">
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div>
        <h3 className="text-base font-semibold text-slate-900 leading-tight">
          {title}
        </h3>
        <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
      </div>
    </div>
    {right}
  </div>
);

const LegendDot: React.FC<{ color: string; label: string }> = ({
  color,
  label,
}) => (
  <div className="flex items-center gap-2">
    <span
      className="w-2.5 h-2.5 rounded-full"
      style={{ backgroundColor: color }}
    />
    <span className="text-xs text-slate-600">{label}</span>
  </div>
);

export default SuperAdminDashboard;